import './index.less'
import WidgetCard from '../../../../../components/WidgetCard'
import CustomAccessTable from '../customAccessTable'
import {
  CustomAccessToIcon,
  FullAccessToIcon,
  ViewOnlyAccessToIcon
} from '../../../components/coloredIcons'
import FullAccessToIconSvg from '../../../../../assets/inlineSvg/roles-full-access.svg'
import FullAccessToIconDataStewardSvg from '../../../../../assets/inlineSvg/roles-full-access-data-steward.svg'
import { IRoleDetails, UserRole } from '../../../constants'
import WidgetInfo from '../../../components/widgetInfo'
import { RootState } from '../../../../../rootReducer'
import { USER_MANAGEMENT_DOCS_URL } from '../../../../../constants'
import React from 'react'
import { Button, Typography } from '@lightbeamai/design-system'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'

interface IProps {
  roleDetails: IRoleDetails
  isDetailsView: boolean
  showErrorText?: string
}

interface IWidgetRoleBasedWidgetInfoMapper {
  [key: string]: {
    widgetTitle: string
    widgetTitleIcon: React.ReactNode
    component?: React.ReactNode
    widgetinfo?: React.ReactNode
  }
}

export const hereLinkButton = (
  <Button
    variant="link"
    className="text-here xs-p-0"
    href={USER_MANAGEMENT_DOCS_URL}
    target="_blank"
  >
    <FormattedMessage id="settings.roles.widget.info.here" />
  </Button>
)

const useWidgetRoleBasedInfoMapper = ({
  isDetailsView,
  roleDetails
}: {
  isDetailsView: boolean
  roleDetails: IRoleDetails
}) => {
  const intl = useIntl()

  return React.useMemo(
    () => ({
      [UserRole.ADMIN]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.fullAccess' }),
        widgetTitleIcon: <FullAccessToIcon />,
        component: (
          <WidgetInfo
            info={
              <div>
                <Typography as="span" type={'medium'}>
                  {intl.formatMessage({
                    id: 'settiings.roles.widget.admin.component.body.fullAccess'
                  })}
                  {hereLinkButton}
                </Typography>
              </div>
            }
            SvgIcon={FullAccessToIconSvg}
          />
        )
      },
      [UserRole.PLATFORM_OWNER]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.customAccess' }),
        widgetTitleIcon: <CustomAccessToIcon />,
        widgetinfo: (
          <Typography as="span" type={'medium'}>
            {intl.formatMessage({ id: 'settings.roles.widget.info.platformViewerOwner' })}
            {hereLinkButton}
          </Typography>
        ),
        component: (
          <CustomAccessTable isDetailsView={isDetailsView} permissions={roleDetails.permissions} />
        )
      },
      [UserRole.PLATFORM_VIEWER]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.viewOnlyAccess' }),
        widgetTitleIcon: <ViewOnlyAccessToIcon />,
        widgetinfo: (
          <Typography as="span" type={'medium'}>
            {intl.formatMessage({ id: 'settings.roles.widget.info.platformViewerOwner' })}
            {hereLinkButton}
          </Typography>
        ),
        component: (
          <CustomAccessTable isDetailsView={isDetailsView} permissions={roleDetails.permissions} />
        )
      },
      [UserRole.DATASOURCE_OWNER]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.customAccess' }),
        widgetTitleIcon: <CustomAccessToIcon />,
        widgetinfo: (
          <Typography as="span" type={'medium'}>
            {intl.formatMessage({ id: 'settings.roles.widget.info.datasourceOwner' })}
            {hereLinkButton}
          </Typography>
        ),
        component: (
          <CustomAccessTable isDetailsView={isDetailsView} permissions={roleDetails.permissions} />
        )
      },
      [UserRole.DATASOURCE_VIEWER]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.viewOnlyAccess' }),
        widgetTitleIcon: <ViewOnlyAccessToIcon />,
        widgetinfo: (
          <Typography as="span" type={'medium'}>
            {intl.formatMessage({ id: 'settings.roles.widget.info.datasourceViewer' })}
            {hereLinkButton}
          </Typography>
        ),
        component: (
          <CustomAccessTable isDetailsView={isDetailsView} permissions={roleDetails.permissions} />
        )
      },
      [UserRole.DATA_STEWARD]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.fullAccess' }),
        widgetTitleIcon: <FullAccessToIcon />,
        component: (
          <WidgetInfo
            info={
              <div>
                <Typography as="span" type={'medium'}>
                  {intl.formatMessage({
                    id: 'settiings.roles.widget.dataSteward.component.body.fullAccess'
                  })}
                  {hereLinkButton}
                </Typography>
              </div>
            }
            SvgIcon={FullAccessToIconDataStewardSvg}
          />
        )
      },
      [UserRole.USER]: {
        widgetTitle: intl.formatMessage({ id: 'settings.roles.widget.title.limitedAccess' }),
        widgetTitleIcon: <CustomAccessToIcon />,
        component: (
          <div className="xs-p-24 widget-info-users-list">
            <Typography>
              Limited access to assigned alerts with all capabilities with following exceptions -
            </Typography>
            <ul>
              <li>No permission to archive objects / files.</li>
              <li>No permission to delete objects / files.</li>
            </ul>
            <Typography>Learn more {hereLinkButton}.</Typography>
          </div>
        )
      }
    }),
    [intl, isDetailsView, roleDetails]
  )
}

const WidgetPermissionAccess = (props: IProps) => {
  const { roleDetails, isDetailsView = true, showErrorText } = props
  const intl = useIntl()

  const widgetRoleBasedWidgetInfoMapper: IWidgetRoleBasedWidgetInfoMapper = useWidgetRoleBasedInfoMapper(
    {
      roleDetails,
      isDetailsView
    }
  )

  const selectedRoleData = roleDetails?.name && widgetRoleBasedWidgetInfoMapper[roleDetails?.name]

  return (
    <div className="xs-mb-32 widget-permission-access-container">
      <WidgetCard
        cardBordered={true}
        widgetTitle={{
          title: selectedRoleData?.widgetTitle,
          bordered: true,
          icon: selectedRoleData?.widgetTitleIcon
        }}
      >
        {selectedRoleData?.widgetinfo && selectedRoleData?.widgetinfo}
        {showErrorText && (
          <Typography type={'medium'} color="error">
            {intl.formatMessage({ id: showErrorText })}
          </Typography>
        )}
        {selectedRoleData?.component && selectedRoleData?.component}
      </WidgetCard>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  roleDetails: state.configureRoles.roleDetails
})

const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(WidgetPermissionAccess)
